"use client";

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CharacterProvider, useCharacter } from '@/components/models/character';
import { 
  Card, 
  CardContent
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useWallet } from '@/contexts/WalletContext';
import { useTheme } from '@/contexts/ThemeProvider';
import { 
  uploadMetadataToCloudflare,
  mintSFT,
  CharacterMetadata
} from '@/api/metaData';

// Подключаем шрифт Changa One с Google Fonts для правильного отображения
const ChangaOneFont = () => (
  <style>
    {`@import url('https://fonts.googleapis.com/css2?family=Changa+One&display=swap');`}
  </style>
);

interface MintPageState {
  snapshotUrl: string;
  fileId: string;
}

// Компонент с основным контентом страницы
const MintPageContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { character } = useCharacter();
  const { walletAddress, isConnected } = useWallet();
  const { isDark } = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [metadataId, setMetadataId] = useState<string | null>(null);
  const [metadataUrl, setMetadataUrl] = useState<string | null>(null);
  const [mintAddress, setMintAddress] = useState<string | null>(null);
  const [mintTxId, setMintTxId] = useState<string | null>(null);
  
  // Состояние для снимка и ID файла
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  
  useEffect(() => {
    // Добавляем отладочную информацию
    console.log('MintPage: Инициализация компонента');
    console.log('MintPage: Состояние location.state:', location.state);
    
    // Пытаемся получить данные из состояния навигации
    const stateData = location.state as { snapshotUrl: string; fileId: string } | null;
    
    // Пытаемся получить данные из localStorage, если не получили из состояния
    const storedDataStr = localStorage.getItem('mintData');
    console.log('MintPage: Данные из localStorage:', storedDataStr);
    
    let mintData = null;
    
    // Проверяем сначала данные из состояния навигации
    if (stateData && stateData.snapshotUrl && stateData.fileId) {
      console.log('MintPage: Используем данные из состояния навигации');
      mintData = stateData;
    } 
    // Затем проверяем данные из localStorage
    else if (storedDataStr) {
      try {
        const parsedData = JSON.parse(storedDataStr);
        if (parsedData && parsedData.snapshotUrl && parsedData.fileId) {
          console.log('MintPage: Используем данные из localStorage');
          mintData = parsedData;
        }
      } catch (error) {
        console.error('MintPage: Ошибка при парсинге данных из localStorage:', error);
      }
    }
    
    // Если данные получены, устанавливаем их в состояние
    if (mintData) {
      console.log('MintPage: Устанавливаем данные снимка и модели');
      setSnapshotUrl(mintData.snapshotUrl);
      setFileId(mintData.fileId);
    } else {
      // Если данных нет, перенаправляем на страницу просмотра модели
      console.log('MintPage: Данные не найдены, перенаправляем на /model-viewer');
      navigate('/model-viewer');
    }
  }, [location, navigate]);
  
  // Проверяем, что у пользователя распределены все 100 очков характеристик
  const totalAllocatedPoints = character.HighKick + character.punch + character.block + 
                               character.evasion + character.LowKick;
  const areStatsComplete = totalAllocatedPoints === 100;
  
  // Функция для загрузки метаданных на Cloudflare
  const handleUploadMetadata = async () => {
    if (!isConnected) {
      setError('Пожалуйста, подключите ваш кошелек для загрузки метаданных');
      return;
    }
    
    if (!areStatsComplete) {
      setError('Пожалуйста, распределите все 100 очков характеристик');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Проверяем наличие адреса кошелька
      if (!walletAddress) {
        throw new Error('Адрес кошелька не найден');
      }
      
      // Подготавливаем метаданные для загрузки на сервер
      const metadata: CharacterMetadata = {
        name: "PeopleFightForMoney Character",
        symbol: "PFMCHAR",
        description: "A 3D character for the PeopleFightForMoney game with unique combat stats",
        image: snapshotUrl || '', // Добавляем пустую строку как fallback
        external_url: "https://peopleformoney.com", 
        seller_fee_basis_points: 0, // 0% роялти
        attributes: [
          { trait_type: "Punch Chance", value: character.punch },
          { trait_type: "High Kick Chance", value: character.HighKick },
          { trait_type: "Low Kick Chance", value: character.LowKick },
          { trait_type: "Block Chance", value: character.block },
          { trait_type: "Evasion Chance", value: character.evasion },
          
          { trait_type: "Punch Damage", value: character.punchDamage },
          { trait_type: "High Kick Damage", value: character.legDamage },
          { trait_type: "Low Kick Damage", value: character.LowKickDamage },
          { trait_type: "Block Power", value: character.blockPower },
          { trait_type: "Damage Reduction", value: character.damageBonus + "%" },
          { trait_type: "Health", value: character.health },
        ],
        collection: {
          name: "FixedStatsCharacters",
          family: "PeopleFightForMoney"
        },
        properties: {
          files: [
            {
              uri: fileId || '', // Добавляем пустую строку как fallback
              type: "model/fbx",
              cdn: true
            },
            {
              uri: snapshotUrl || '',
              type: "image/png"
            }
          ],
          category: "3D Model",
          creators: [
            {
              address: walletAddress,
              share: 100
            }
          ],
          model: fileId || '' // Добавляем пустую строку как fallback
        }
      };
      
      // Загружаем метаданные на сервер Cloudflare
      const result = await uploadMetadataToCloudflare(metadata, walletAddress);
      
      if (result.success && result.metadataId) {
        setSuccess(`Метаданные успешно загружены! Вы можете чеканить SFT.`);
        setMetadataId(result.metadataId);
        setMetadataUrl(result.metadataUrl || null);
      } else {
        throw new Error(result.error || 'Ошибка при загрузке метаданных');
      }
    } catch (error) {
      console.error('Ошибка загрузки метаданных:', error);
      
      // Обрабатываем ошибки сетевого соединения
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        setError('Не удалось подключиться к серверу для загрузки метаданных. Проверьте подключение к интернету или попробуйте позже.');
      } else {
        setError(error instanceof Error ? error.message : 'Неизвестная ошибка при загрузке метаданных');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Функция для чеканки SFT
  const handleMintSFT = async () => {
    if (!metadataId) {
      setError('Сначала загрузите метаданные на сервер');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Проверяем наличие адреса кошелька
      if (!walletAddress) {
        throw new Error('Адрес кошелька не найден');
      }
      
      // Чеканим SFT с использованием метаданных
      const result = await mintSFT(metadataId, walletAddress);
      
      if (result.success) {
        setSuccess('SFT успешно создан!');
        if (result.mintAddress) {
          setMintAddress(result.mintAddress);
        }
        if (result.txId) {
          setMintTxId(result.txId);
        }
      } else {
        throw new Error(result.error || 'Ошибка при чеканке SFT');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ошибка при чеканке SFT');
    } finally {
      setIsUploading(false);
    }
  };
  
  if (!snapshotUrl || !fileId) {
    return null; // Редирект обработается в useEffect
  }
  
  // Определяем, какие иконки использовать в зависимости от темы
  // Для темной темы используются иконки с суффиксом W (white)
  // Для светлой темы используются иконки с суффиксом B (black)
  const iconSet = isDark ? {
    punch: '/icons/punchB.png',
    highKick: '/icons/highKickB.png',
    lowKick: '/icons/lowKickB.png',
    block: '/icons/BlockB.png',
    evasion: '/icons/evasionB.png',
    health: '/icons/healthB.png'
  } : {
    punch: '/icons/punchW.png',
    highKick: '/icons/highKickW.png',
    lowKick: '/icons/lowKickW.png',
    block: '/icons/BlockW.png',
    evasion: '/icons/evasionW.png',
    health: '/icons/healthW.png'
  };
 
  // Цвета для процентов каждого действия в соответствии с макетом
  const percentColors = {
    punch: isDark ? 'text-white' : 'text-foreground',
    highKick: isDark ? 'text-white' : 'text-foreground',
    lowKick: isDark ? 'text-white' : 'text-foreground',
    block: isDark ? 'text-white' : 'text-foreground',
    evasion: isDark ? 'text-white' : 'text-foreground'
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <ChangaOneFont />
      <Card className="flex-1 rounded-none shadow-none bg-background">
        <CardContent className="p-0">
          <div className="container max-w-6xl mx-auto py-6">
            {/* Три отдельных блока вместо одного */}
            <div className="grid grid-cols-3 gap-6 mb-10">
              {/* Блок 1: Изображение персонажа */}
              <Card className="bg-card border shadow-md overflow-hidden">
                <CardContent className="p-[5px]">
                  <div className="aspect-square overflow-hidden rounded">
                    <img 
                      src={snapshotUrl} 
                      alt="Character Preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* Блок 2: Характеристики персонажа */}
              <Card className="bg-card border shadow-md overflow-hidden flex items-center">
                <CardContent className="py-[20px] px-[20px] w-full h-full flex flex-col justify-between">
                  {/* Punch */}
                  <div className="flex items-center mb-[15px]">
                    {/* Процент шанса удара */}
                    <div className="w-[50px]">
                      <span className={`text-2xl font-['Changa_One'] ${percentColors.punch}`} style={{fontFamily: 'Changa One, cursive'}}>
                        {character.punch}%
                      </span>
                    </div>
                    {/* Иконка */}
                    <div className="w-[32px] h-[32px] mx-[10px]">
                      <img src={iconSet.punch} alt="Punch" className="w-full h-full" />
                    </div>
                    {/* Название и урон */}
                    <div className="flex justify-between flex-1 items-center">
                      <span className="text-muted-foreground">Punch</span>
                      <span className="text-2xl font-['Changa_One']" style={{fontFamily: 'Changa One, cursive'}}>{character.punchDamage}</span>
                    </div>
                  </div>
                  
                  {/* High Kick */}
                  <div className="flex items-center mb-[15px]">
                    {/* Процент шанса удара */}
                    <div className="w-[50px]">
                      <span className={`text-2xl font-['Changa_One'] ${percentColors.highKick}`} style={{fontFamily: 'Changa One, cursive'}}>
                        {character.HighKick}%
                      </span>
                    </div>
                    {/* Иконка */}
                    <div className="w-[32px] h-[32px] mx-[10px]">
                      <img src={iconSet.highKick} alt="High Kick" className="w-full h-full" />
                    </div>
                    {/* Название и урон */}
                    <div className="flex justify-between flex-1 items-center">
                      <span className="text-muted-foreground">High kick</span>
                      <span className="text-2xl font-['Changa_One']" style={{fontFamily: 'Changa One, cursive'}}>{character.legDamage}</span>
                    </div>
                  </div>
                  
                  {/* Low Kick */}
                  <div className="flex items-center mb-[15px]">
                    {/* Процент шанса удара */}
                    <div className="w-[50px]">
                      <span className={`text-2xl font-['Changa_One'] ${percentColors.lowKick}`} style={{fontFamily: 'Changa One, cursive'}}>
                        {character.LowKick}%
                      </span>
                    </div>
                    {/* Иконка */}
                    <div className="w-[32px] h-[32px] mx-[10px]">
                      <img src={iconSet.lowKick} alt="Low Kick" className="w-full h-full" />
                    </div>
                    {/* Название и урон */}
                    <div className="flex justify-between flex-1 items-center">
                      <span className="text-muted-foreground">Kick</span>
                      <span className="text-2xl font-['Changa_One']" style={{fontFamily: 'Changa One, cursive'}}>{character.LowKickDamage}</span>
                    </div>
                  </div>
                  
                  {/* Block */}
                  <div className="flex items-center mb-[15px]">
                    {/* Процент шанса блока */}
                    <div className="w-[50px]">
                      <span className={`text-2xl font-['Changa_One'] ${percentColors.block}`} style={{fontFamily: 'Changa One, cursive'}}>
                        {character.block}%
                      </span>
                    </div>
                    {/* Иконка */}
                    <div className="w-[32px] h-[32px] mx-[10px]">
                      <img src={iconSet.block} alt="Block" className="w-full h-full" />
                    </div>
                    {/* Название и сила блока */}
                    <div className="flex justify-between flex-1 items-center">
                      <span className="text-muted-foreground">Block</span>
                      <span className="text-2xl font-['Changa_One']" style={{fontFamily: 'Changa One, cursive'}}>{character.blockPower}</span>
                    </div>
                  </div>
                  
                  {/* Evasion */}
                  <div className="flex items-center">
                    {/* Процент шанса уклонения */}
                    <div className="w-[50px]">
                      <span className={`text-2xl font-['Changa_One'] ${percentColors.evasion}`} style={{fontFamily: 'Changa One, cursive'}}>
                        {character.evasion}%
                      </span>
                    </div>
                    {/* Иконка */}
                    <div className="w-[32px] h-[32px] mx-[10px]">
                      <img src={iconSet.evasion} alt="Evasion" className="w-full h-full" />
                    </div>
                    {/* Название и бонус */}
                    <div className="flex justify-between flex-1 items-center">
                      <span className="text-muted-foreground">Evasion</span>
                      <span className="text-2xl font-['Changa_One']" style={{fontFamily: 'Changa One, cursive'}}>{character.damageBonus}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Блок 3: Здоровье и специальные эффекты */}
              <Card className="bg-card border shadow-md overflow-hidden">
                <CardContent className="pt-[10px] pl-[20px] pr-[20px]">
                  {/* Здоровье */}
                  <div className="mb-8">
                    <div className="flex items-center mb-1">
                      <span className="text-sm text-muted-foreground">Health points</span>
                    </div>
                    <div className="font-['Changa_One'] text-4xl" style={{fontFamily: 'Changa One, cursive'}}>{character.health}</div>
                  </div>
                  
                  {/* Специальные эффекты */}
                  <div className="mt-auto">
                    <div className="bg-secondary p-3 rounded">
                      <div className="text-sm text-muted-foreground mb-1">Special effects:</div>
                      <div>
                        Снижение урона: <span className="font-medium">{character.damageBonus}%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Шаги для создания NFT */}
            <div className="grid grid-cols-2 gap-6 mt-10">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-medium mb-4">Step 1: Подготовка персонажа</h3>
                  <p className="text-muted-foreground text-sm mb-2">
                    Проверьте, что все характеристики распределены правильно.
                  </p>
                  <div className="text-sm">
                    Распределено очков: <span className="font-medium">{totalAllocatedPoints}/100</span>
                  </div>
                  {error && (
                    <div className="mt-4 text-red-500 text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="mt-4 text-green-500 text-sm">
                      {success}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-medium mb-4">Step 2: Загрузить метаданные</h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Загрузите метаданные вашего персонажа на серверы Cloudflare в формате Metaplex.
                  </p>
                  
                  <Button 
                    onClick={handleUploadMetadata}
                    disabled={isUploading || !isConnected}
                    variant="secondary"
                    className="w-full"
                  >
                    {isUploading ? 'Загрузка...' : 'Загрузить метаданные'}
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="col-span-2">
                <CardContent className="p-6 flex flex-col">
                  <h3 className="text-lg font-medium mb-4">Step 3: Чеканка SFT</h3>
                  <div className="flex-grow mb-4">
                    <p className="text-muted-foreground text-sm">
                      После успешной загрузки метаданных вы можете создать SFT вашего персонажа.
                    </p>
                    
                    {metadataId && (
                      <div className="mt-4">
                        <p className="text-sm mb-2">
                          ID метаданных: <span className="font-mono text-xs">{metadataId}</span>
                        </p>
                        {metadataUrl && (
                          <p className="text-sm mb-2">
                            URL метаданных: <span className="font-mono text-xs">{metadataUrl}</span>
                          </p>
                        )}
                        <Button 
                          onClick={handleMintSFT}
                          disabled={isUploading || !metadataId}
                          variant="default"
                          className="w-full"
                        >
                          {isUploading ? 'Обработка...' : 'Чеканить SFT'}
                        </Button>
                      </div>
                    )}
                    
                    {mintAddress && (
                      <div className="mt-4">
                        <p className="text-sm">
                          Адрес SFT: <span className="font-mono text-xs">{mintAddress}</span>
                        </p>
                        {mintTxId && (
                          <p className="text-sm mt-1">
                            Транзакция чеканки: <span className="font-mono text-xs">{mintTxId}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Обертка для обеспечения контекста CharacterProvider
const MintPage: React.FC = () => {
  return (
    <CharacterProvider>
      <ChangaOneFont />
      <MintPageContent />
    </CharacterProvider>
  );
};

export default MintPage; 